import {announce} from '@github-ui/aria-live'
import {Heading} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useRef, useState} from 'react'

import {apiUpdateColumn} from '../../../api/columns/api-update-column'
import {type MemexColumnDataType, SystemColumnId} from '../../../api/columns/contracts/memex-column'
import {getEnabledFeatures} from '../../../helpers/feature-flags'
import {CommitState} from '../../../hooks/use-autosave'
import {useSingleSelectOptions} from '../../../hooks/use-single-select-options'
import {useStatusDefaultValue} from '../../../hooks/use-status-default-value'
import type {ColumnModelForDataType} from '../../../models/column-model'
import {Resources} from '../../../strings'
import {AutosaveMessage} from '../autosave-message'
import {SingleSelectForm} from './single-select-form'
import styles from './single-select-options.module.css'

type ColumnSettingsViewProps = {
  column: ColumnModelForDataType<typeof MemexColumnDataType.SingleSelect>
  disabled?: boolean
}

export const SingleSelectOptions: React.FC<ColumnSettingsViewProps> = ({column: externalColumn, disabled = false}) => {
  const {memex_automation_enabled: memexAutomationEnabled} = getEnabledFeatures()
  const isStatusField = externalColumn.id === SystemColumnId.Status
  // Status fields use the "Item added" project workflow as their default value backing store.
  // Project workflows may be disabled on Enterprise, in which case the default would never
  // apply — so we don't surface the option at all.
  const canSetStatusDefaultValue = isStatusField && !!memexAutomationEnabled
  // Regular single-select fields use column settings.
  const canSetRegularDefaultValue = !isStatusField

  const containerRef = useRef<HTMLDivElement>(null)
  // Returns focus to a restored option when a deletion is cancelled via the confirmation dialog.
  const focusOption = useCallback((index: number) => {
    requestAnimationFrame(() =>
      containerRef.current?.querySelector<HTMLButtonElement>(`button[data-option-index='${index}']`)?.focus(),
    )
  }, [])

  const {
    commitState,
    // column settings
    options,
    onDrop,
    addOption,
    canRemoveOption,
    removeOption,
    updateOption,
    // error message
    errorMessage,
  } = useSingleSelectOptions({externalColumn, onDeletionCancelled: focusOption})

  const {defaultValueOptionId, isWorkflowEnabled, itemAddedWorkflow, setDefaultValue} =
    useStatusDefaultValue(externalColumn)

  const [columnSettingsDefaultValue, setColumnSettingsDefaultValue] = useState<string | number | undefined>(
    (externalColumn.settings as {width: number; options: Array<unknown>; defaultValue?: string | number})?.defaultValue,
  )

  const onRegularDefaultValueChange = useCallback(
    async (newValue: string | number | undefined) => {
      const valueToSave = newValue === '' ? undefined : newValue
      setColumnSettingsDefaultValue(valueToSave)

      try {
        await apiUpdateColumn({
          memexProjectColumnId: externalColumn.id,
          settings: {
            // Send null to explicitly unset the default value, as undefined gets omitted from JSON
            defaultValue: valueToSave === undefined ? null : valueToSave,
          },
        })
        announce(Resources.changesSaved)
      } catch {
        announce(Resources.genericErrorMessage)
      }
    },
    [externalColumn.id],
  )

  const onStatusDefaultValueChange = useCallback(
    async (newValue: string | undefined) => {
      try {
        await setDefaultValue(newValue)
        announce(Resources.changesSaved)
      } catch {
        announce(Resources.genericErrorMessage)
      }
    },
    [setDefaultValue],
  )

  useEffect(() => {
    if (commitState === CommitState.Successful) {
      announce('Saved!')
    }
  }, [commitState])

  return (
    <>
      <Heading as="h3" className={styles.OptionsHeading} aria-label={`${externalColumn.name} options`}>
        Options
      </Heading>
      <div ref={containerRef}>
        <SingleSelectForm
          options={options}
          onAddOption={addOption}
          onUpdateOption={updateOption}
          onRemoveOption={removeOption}
          canRemoveOption={canRemoveOption}
          onDrop={onDrop}
          className={clsx(commitState === CommitState.Failed && styles.SingleSelectForm)}
          noDuplicatedOptions={false}
          disabled={disabled}
          enableNameLengthValidation={false}
          enableDescriptionLengthValidation={false}
          disallowCommasInName={false}
          defaultValueId={
            canSetStatusDefaultValue && isWorkflowEnabled
              ? defaultValueOptionId
              : canSetRegularDefaultValue &&
                  columnSettingsDefaultValue !== undefined &&
                  columnSettingsDefaultValue !== null
                ? String(columnSettingsDefaultValue)
                : undefined
          }
          onSetDefaultValue={
            canSetStatusDefaultValue && !disabled
              ? onStatusDefaultValueChange
              : canSetRegularDefaultValue && !disabled
                ? onRegularDefaultValueChange
                : undefined
          }
        />
      </div>

      {canSetStatusDefaultValue && defaultValueOptionId !== undefined && (
        <div className={styles.WorkflowEnabledStatus}>
          {itemAddedWorkflow?.name ?? Resources.itemAddedWorkflowFallbackName}:{' '}
          {isWorkflowEnabled ? Resources.workflowActive : Resources.workflowDisabled}
        </div>
      )}

      {!disabled && <AutosaveMessage commitState={commitState} errorMessage={errorMessage} />}
    </>
  )
}
